import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

type Result = {
  title: string;
  description?: string;
  path: string;
  group: string;
};

const slugify = (t: string) =>
  t
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

interface SearchDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}

const SearchDrawer = ({ open, onOpenChange, returnFocusRef }: SearchDrawerProps) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const newsEnabled = settings.news_enabled !== "false";

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const { data: games = [] } = useQuery({
    queryKey: ["search-games"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("games")
        .select("id, title, hero_tagline, short_description")
        .eq("is_published", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: articles = [] } = useQuery({
    queryKey: ["search-news"],
    enabled: open && newsEnabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, title, slug, category")
        .eq("is_published", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const results = useMemo<Result[]>(() => {
    const byTitle = (a: Result, b: Result) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

    const staticPages: Result[] = [
      { title: "Home", description: "Rogue Skull Games homepage", path: "/", group: "Pages" },
      { title: "Games", description: "Browse all of our games", path: "/games", group: "Pages" },
      { title: "About", description: "Meet the crew behind Rogue Skull Games", path: "/about", group: "Pages" },
      ...(newsEnabled ? [{ title: "News", description: "Latest updates and announcements", path: "/news", group: "Pages" }] : []),
      { title: "Bag", description: "Review the items in your bag", path: "/cart", group: "Pages" },
      { title: "Use of AI", description: "How we use AI in our work", path: "/ai-usage", group: "Pages" },
      {
        title: "Return Policy",
        description: "Cancellations and refunds for on-demand orders",
        path: "/return-policy",
        group: "Pages",
      },
      {
        title: "Privacy Policy",
        description: "How we collect, use, and protect your information",
        path: "/privacy-policy",
        group: "Pages",
      },
      {
        title: "Terms of Use",
        description: "The terms that govern use of our site and orders",
        path: "/terms-of-use",
        group: "Pages",
      },
    ].sort(byTitle);

    const gameResults: Result[] = games.map((g: any) => ({
      title: g.title,
      description: g.hero_tagline || g.short_description || undefined,
      path: `/games/${slugify(g.title)}`,
      group: "Games",
    })).sort(byTitle);

    const newsResults: Result[] = newsEnabled
      ? articles.map((a: any) => ({
          title: a.title,
          description: a.category || undefined,
          path: `/news/${a.slug}`,
          group: "News",
        })).sort(byTitle)
      : [];

    const all = [...gameResults, ...staticPages, ...newsResults];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) => r.title.toLowerCase().includes(q) || (r.description ?? "").toLowerCase().includes(q)
    );
  }, [games, articles, query, newsEnabled]);

  const grouped = useMemo(() => {
    return results.reduce<Record<string, Result[]>>((acc, r) => {
      (acc[r.group] ||= []).push(r);
      return acc;
    }, {});
  }, [results]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
        className="top-[var(--topbar-height,0px)] h-[calc(100dvh-var(--topbar-height,0px))] w-full sm:max-w-md bg-background text-foreground p-0 flex flex-col [&>button]:hidden"
      >
        <SheetTitle className="sr-only">Search the site</SheetTitle>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <SearchIcon className="w-5 h-5 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const links = resultsRef.current?.querySelectorAll<HTMLAnchorElement>("a[href]");
                const target = e.key === "ArrowDown" ? links?.[0] : links?.[links.length - 1];
                target?.focus();
              }
              if (e.key === "Enter" && results.length > 0) go(results[0].path);
            }}
            placeholder="Search pages, games, news..."
            aria-label="Search the site"
            className="min-w-0 flex-1 rounded bg-transparent text-foreground placeholder:text-muted-foreground text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Close search"
            className="min-h-11 min-w-11 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div
          ref={resultsRef}
          className="flex-1 overflow-y-auto px-2 py-3"
          onKeyDown={(event) => {
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>("a[href]"));
            const index = links.findIndex((link) => link === document.activeElement);
            if (index < 0) return;
            event.preventDefault();
            if ((event.key === "ArrowUp" && index === 0) || (event.key === "ArrowDown" && index === links.length - 1)) {
              inputRef.current?.focus();
              return;
            }
            const next = event.key === "Home" ? 0 : event.key === "End" ? links.length - 1 : index + (event.key === "ArrowDown" ? 1 : -1);
            links[next]?.focus();
          }}
        >
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No results for “{query}”.
            </p>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="mb-4">
                <p className="px-4 pb-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{group}</p>
                {items.map((r) => (
                  <Button
                    asChild
                    variant="ghost"
                    key={r.path + r.title}
                    className="block h-auto min-h-11 w-full whitespace-normal text-left px-4 py-3 rounded-lg hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-offset-0 transition-colors group"
                  >
                    <Link to={r.path} onClick={() => onOpenChange(false)}>
                    <span className="block text-sm font-medium group-hover:text-blood-red transition-colors">
                      {r.title}
                    </span>
                    {r.description && (
                      <span className="block text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.description}</span>
                    )}
                    </Link>
                  </Button>
                ))}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SearchDrawer;
